import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../home/components/Footer';

export default function CaseBankDetailPage() {
  const [activeTab, setActiveTab] = useState('overview');

  const beforeAfterData = [
    {
      metric: '合同处理效率',
      before: '人工处理，每份合同平均2-3天',
      after: '自动化处理，每份合同平均2-3小时',
      improvement: '300%',
      icon: 'ri-time-line'
    },
    {
      metric: '年度成本节省',
      before: '纸张、印刷、存储成本高昂',
      after: '数字化无纸化办公',
      improvement: '500万+',
      icon: 'ri-money-dollar-circle-line'
    },
    {
      metric: '合同查询时间',
      before: '人工翻找，平均30-60分钟',
      after: '秒级智能检索',
      improvement: '99.9%',
      icon: 'ri-search-line'
    },
    {
      metric: '错误率降低',
      before: '人工处理错误率约5%',
      after: '系统自动化处理错误率0.1%',
      improvement: '98%',
      icon: 'ri-error-warning-line'
    },
    {
      metric: '存储空间节省',
      before: '大量物理存储空间占用',
      after: '云端数字化存储',
      improvement: '90%',
      icon: 'ri-database-2-line'
    },
    {
      metric: '客户满意度',
      before: '传统流程客户满意度78%',
      after: '数字化流程客户满意度96%',
      improvement: '18%',
      icon: 'ri-customer-service-2-line'
    }
  ];

  const implementationSteps = [
    {
      phase: '第一阶段',
      title: '需求调研与系统设计',
      duration: '2周',
      description: '深入了解银行现有合同管理流程，设计数字化解决方案架构',
      deliverables: ['需求分析报告', '系统架构设计', 'UI/UX设计稿', '技术方案确认'],
      status: 'completed'
    },
    {
      phase: '第二阶段', 
      title: '核心系统开发',
      duration: '6周',
      description: '开发PDF自动化生成、数字签名集成、智能归档等核心功能',
      deliverables: ['PDF生成引擎', '电子签名模块', '智能归档系统', '用户权限管理'],
      status: 'completed'
    },
    {
      phase: '第三阶段',
      title: '集成测试与试点',
      duration: '3周', 
      description: '与银行现有系统集成，在试点部门进行功能测试和优化',
      deliverables: ['系统集成完成', '功能测试报告', '性能优化方案', '试点运行数据'],
      status: 'completed'
    },
    {
      phase: '第四阶段',
      title: '全面部署与培训',
      duration: '4周',
      description: '全行范围部署系统，开展员工培训和技术支持',
      deliverables: ['全行系统部署', '员工培训完成', '操作手册交付', '技术支持体系'],
      status: 'completed'
    }
  ];

  const keyFeatures = [
    {
      title: 'PDF智能生成',
      description: '基于模板自动生成标准化合同文档，支持多种合同类型',
      benefits: ['标准化程度提升95%', '生成速度提升20倍', '格式一致性100%'],
      icon: 'ri-file-text-line'
    },
    {
      title: '数字签名集成',
      description: '集成多家数字签名服务商，确保签名的法律效力和安全性',
      benefits: ['签名效率提升10倍', '安全等级达银行级', '法律合规100%'],
      icon: 'ri-quill-pen-line'
    },
    {
      title: '智能归档检索',
      description: 'AI驱动的智能分类和全文检索，支持多维度快速查找',
      benefits: ['检索速度提升1000倍', 'AI准确率98%', '支持模糊搜索'],
      icon: 'ri-archive-line'
    },
    {
      title: '流程自动化',
      description: '端到端业务流程自动化，从合同生成到归档全程无人工干预',
      benefits: ['人工成本降低80%', '处理效率提升300%', '24/7不间断服务'],
      icon: 'ri-settings-3-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-600 text-white py-24 overflow-hidden"
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/812cc194b6a346d217e92adaf4864002.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-blue-900/80"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="flex items-center mb-6">
              <span className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-semibold mr-4">
                成功案例详情
              </span>
              <span className="text-blue-200">银行信贷</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              某大型银行数字化合同管理系统
            </h1>
            <p className="text-xl lg:text-2xl text-blue-100 mb-8 leading-relaxed">
              通过部署Adobe PDF Library SDK，实现全行业务合同数字化管理，年节省成本超过500万元，合同处理效率提升300%
            </p>
            <div className="flex flex-wrap gap-6">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
                <div className="text-2xl font-bold mb-1">500万+</div>
                <div className="text-blue-200 text-sm">年节省成本</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
                <div className="text-2xl font-bold mb-1">300%</div>
                <div className="text-blue-200 text-sm">效率提升</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
                <div className="text-2xl font-bold mb-1">99.9%</div>
                <div className="text-blue-200 text-sm">查询时间缩短</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
                <div className="text-2xl font-bold mb-1">15天</div>
                <div className="text-blue-200 text-sm">部署周期</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="sticky top-0 z-10 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex space-x-8">
            {[
              { id: 'overview', label: '项目概览', icon: 'ri-eye-line' },
              { id: 'comparison', label: '前后对比', icon: 'ri-contrast-2-line' },
              { id: 'features', label: '核心功能', icon: 'ri-function-line' },
              { id: 'implementation', label: '实施过程', icon: 'ri-roadmap-line' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-semibold text-sm transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className={`${tab.icon} mr-2`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Project Overview */}
      {activeTab === 'overview' && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-8">项目背景</h2>
                
                <div className="space-y-6 mb-12">
                  <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                    <h3 className="font-bold text-red-900 mb-3 flex items-center">
                      <i className="ri-error-warning-line mr-2"></i>
                      面临的挑战
                    </h3>
                    <ul className="space-y-2 text-red-800">
                      <li className="flex items-start">
                        <i className="ri-close-circle-line text-red-500 mt-0.5 mr-2"></i>
                        传统纸质合同管理效率低下，处理周期长
                      </li>
                      <li className="flex items-start">
                        <i className="ri-close-circle-line text-red-500 mt-0.5 mr-2"></i>
                        存储成本高昂，占用大量物理空间
                      </li>
                      <li className="flex items-start">
                        <i className="ri-close-circle-line text-red-500 mt-0.5 mr-2"></i>
                        合同查询困难，需要人工翻找
                      </li>
                      <li className="flex items-start">
                        <i className="ri-close-circle-line text-red-500 mt-0.5 mr-2"></i>
                        人工处理错误率高，合规风险大
                      </li>
                    </ul>
                  </div>

                  <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
                    <h3 className="font-bold text-green-900 mb-3 flex items-center">
                      <i className="ri-lightbulb-line mr-2"></i>
                      解决方案
                    </h3>
                    <ul className="space-y-2 text-green-800">
                      <li className="flex items-start">
                        <i className="ri-check-circle-line text-green-500 mt-0.5 mr-2"></i>
                        PDF自动化生成与标准化模板
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-circle-line text-green-500 mt-0.5 mr-2"></i>
                        数字签名集成，确保法律效力
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-circle-line text-green-500 mt-0.5 mr-2"></i>
                        智能归档与全文检索系统
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-circle-line text-green-500 mt-0.5 mr-2"></i>
                        端到端流程自动化
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">项目规模</h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <div className="text-3xl font-bold text-blue-600 mb-1">500+</div>
                      <div className="text-gray-600">分支机构</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-blue-600 mb-1">10万+</div>
                      <div className="text-gray-600">员工用户</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-blue-600 mb-1">100万+</div>
                      <div className="text-gray-600">年合同量</div>
                    </div>
                    <div>
                      <div className="text-3xl font-bold text-blue-600 mb-1">50+</div>
                      <div className="text-gray-600">合同类型</div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20banking%20office%20with%20digital%20contract%20management%20workflow%20visualization%2C%20modern%20bank%20employees%20working%20with%20electronic%20documents%20on%20multiple%20screens%2C%20clean%20corporate%20environment%20with%20digital%20transformation%20graphics%20and%20contract%20processing%20automation%20interface&width=800&height=600&seq=bank-workflow&orientation=portrait"
                  alt="数字化合同管理工作流程"
                  className="w-full h-96 object-cover object-top rounded-xl shadow-2xl mb-8"
                />
                
                <div className="bg-blue-50 rounded-xl p-8">
                  <h3 className="text-xl font-bold text-blue-900 mb-4 flex items-center">
                    <i className="ri-trophy-line text-blue-600 mr-2"></i>
                    项目成果
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">合同处理效率</span>
                      <span className="font-bold text-blue-600">提升 300%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">年度成本节省</span>
                      <span className="font-bold text-blue-600">500万+ 元</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">查询响应时间</span>
                      <span className="font-bold text-blue-600">缩短至 秒级</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">错误率降低</span>
                      <span className="font-bold text-blue-600">98%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Before/After Comparison */}
      {activeTab === 'comparison' && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                数字化转型前后对比
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                真实数据展示数字化合同管理系统带来的显著改善
              </p>
            </div>

            <div className="space-y-12">
              {beforeAfterData.map((item, index) => (
                <div key={index} className="bg-white rounded-xl shadow-xl p-8">
                  <div className="flex items-center mb-8">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                      <i className={`${item.icon} text-3xl text-blue-600`}></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">{item.metric}</h3>
                  </div>

                  <div className="grid lg:grid-cols-3 gap-8">
                    <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-400">
                      <h4 className="font-bold text-red-900 mb-3 flex items-center">
                        <i className="ri-close-circle-line text-red-500 mr-2"></i>
                        改造前
                      </h4>
                      <p className="text-red-800">{item.before}</p>
                    </div>

                    <div className="bg-green-50 rounded-lg p-6 border-l-4 border-green-400">
                      <h4 className="font-bold text-green-900 mb-3 flex items-center">
                        <i className="ri-check-circle-line text-green-500 mr-2"></i>
                        改造后
                      </h4>
                      <p className="text-green-800">{item.after}</p>
                    </div>

                    <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-400 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-blue-600 mb-2">
                          {item.improvement}
                        </div>
                        <div className="text-blue-800 font-semibold">
                          {item.metric.includes('成本') ? '节省' : '提升'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* ROI Calculator */}
            <div className="mt-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-xl p-8">
              <div className="text-center">
                <h3 className="text-3xl font-bold mb-6">投资回报率（ROI）分析</h3>
                <div className="grid md:grid-cols-4 gap-8">
                  <div>
                    <div className="text-4xl font-bold mb-2">1500万</div>
                    <div className="text-blue-100">初始投资</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold mb-2">500万</div>
                    <div className="text-blue-100">年节省成本</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold mb-2">3年</div>
                    <div className="text-blue-100">投资回收期</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold mb-2">333%</div>
                    <div className="text-blue-100">5年ROI</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Core Features */}
      {activeTab === 'features' && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                核心功能模块
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                基于Adobe PDF Library SDK构建的强大功能体系
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12">
              {keyFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-xl p-8">
                  <div className="flex items-start mb-6">
                    <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mr-6 flex-shrink-0">
                      <i className={`${feature.icon} text-3xl text-blue-600`}></i>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                      <i className="ri-trophy-line text-green-500 mr-2"></i>
                      核心收益
                    </h4>
                    <div className="space-y-3">
                      {feature.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-center">
                          <i className="ri-check-line text-green-500 mr-2"></i>
                          <span className="text-gray-700">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Technology Architecture */}
            <div className="mt-16 bg-gray-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">技术架构图</h3>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white rounded-lg p-6 text-center">
                  <i className="ri-window-line text-4xl text-blue-600 mb-4"></i>
                  <h4 className="font-bold text-gray-900 mb-2">前端应用层</h4>
                  <p className="text-gray-600 text-sm">Web应用界面、移动端APP、客户门户</p>
                </div>
                <div className="bg-white rounded-lg p-6 text-center">
                  <i className="ri-server-line text-4xl text-green-600 mb-4"></i>
                  <h4 className="font-bold text-gray-900 mb-2">业务服务层</h4>
                  <p className="text-gray-600 text-sm">PDF处理引擎、签名服务、工作流引擎</p>
                </div>
                <div className="bg-white rounded-lg p-6 text-center">
                  <i className="ri-database-2-line text-4xl text-purple-600 mb-4"></i>
                  <h4 className="font-bold text-gray-900 mb-2">数据存储层</h4>
                  <p className="text-gray-600 text-sm">关系数据库、文档存储、备份系统</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Implementation Process */}
      {activeTab === 'implementation' && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                实施时间轴
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                15周完整实施过程，确保项目顺利交付
              </p>
            </div>

            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-8 top-8 bottom-8 w-1 bg-blue-200"></div>
              
              <div className="space-y-12">
                {implementationSteps.map((step, index) => (
                  <div key={index} className="relative flex items-start">
                    {/* Timeline Dot */}
                    <div className="absolute left-4 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    
                    <div className="ml-20 bg-white rounded-xl shadow-lg p-8 flex-1">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-semibold">
                            {step.phase}
                          </span>
                          <h3 className="text-2xl font-bold text-gray-900 mt-2">{step.title}</h3>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-500">耗时</div>
                          <div className="font-bold text-blue-600">{step.duration}</div>
                        </div>
                      </div>
                      
                      <p className="text-gray-600 mb-6">{step.description}</p>
                      
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h4 className="font-bold text-gray-900 mb-3">主要交付物</h4>
                        <div className="grid md:grid-cols-2 gap-3">
                          {step.deliverables.map((deliverable, idx) => (
                            <div key={idx} className="flex items-center">
                              <i className="ri-file-check-line text-green-500 mr-2"></i>
                              <span className="text-gray-700">{deliverable}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Project Team */}
            <div className="mt-16 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">项目团队构成</h3>
              <div className="grid md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-user-star-line text-2xl text-blue-600"></i>
                  </div>
                  <div className="font-bold text-gray-900">项目经理</div>
                  <div className="text-gray-600 text-sm">1人</div>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-code-line text-2xl text-green-600"></i>
                  </div>
                  <div className="font-bold text-gray-900">开发工程师</div>
                  <div className="text-gray-600 text-sm">8人</div>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-shield-check-line text-2xl text-purple-600"></i>
                  </div>
                  <div className="font-bold text-gray-900">测试工程师</div>
                  <div className="text-gray-600 text-sm">3人</div>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-customer-service-2-line text-2xl text-orange-600"></i>
                  </div>
                  <div className="font-bold text-gray-900">技术支持</div>
                  <div className="text-gray-600 text-sm">2人</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Client Testimonial */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="bg-white rounded-xl shadow-xl p-12">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-double-quotes-l text-4xl text-blue-600"></i>
            </div>
            <blockquote className="text-2xl text-gray-800 font-medium mb-8 leading-relaxed">
              "通过部署数字化合同管理系统，我们不仅实现了业务效率的大幅提升，更重要的是为客户提供了更加便捷、安全的服务体验。系统的稳定性和安全性完全满足银行级要求。"
            </blockquote>
            <div className="flex items-center justify-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mr-4">
                <i className="ri-user-line text-2xl text-white"></i>
              </div>
              <div className="text-left">
                <div className="font-bold text-gray-900">张总经理</div>
                <div className="text-gray-600">某大型银行 信息技术部</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Next Steps CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            开启您的数字化转型之旅
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            让我们为您量身定制PDF数字化解决方案，实现与此案例同样的显著成果
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/contact" className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer">
              免费咨询方案
            </a>
            <a href="/products" className="bg-blue-500 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-400 transition-colors whitespace-nowrap cursor-pointer border-2 border-white">
              查看产品详情
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}